// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2024 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import useAppSelector from "@insite/client-framework/Common/Hooks/useAppSelector";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import { CustomAppState } from "@sc/App/Store/Reducers";
import { color } from "@sc/XDL/Utilities";
import * as React from "react";
import { useRef } from "react";
import { Transition } from "react-transition-group";
import { TransitionStatus } from "react-transition-group/Transition";
import styled, { css, ThemeProps } from "styled-components";

const StyledOverlayComponent = styled.div<
    { transitionState: TransitionStatus; fullOverlay: boolean } & ThemeProps<BaseTheme>
>`
    position: fixed;
    right: 0;
    bottom: 0;
    top: 0;
    left: 0;
    background-color: ${color("common.ui7")};
    z-index: ${({ theme, fullOverlay }: any) => (fullOverlay ? theme.zIndex.modal - 1 : theme.zIndex.styledOverlay)};
    
    transition: opacity ease-in-out 150ms;
    ${({ transitionState }) => {
        switch (transitionState) {
            case "entering":
                return "opacity: 0;";
            case "entered":
                return "opacity: 0.33;";
            case "exiting":
            case "exited":
                return "opacity: 0;";
            default:
                return "";
        }
    }}
`;

const StyledOverlay = () => {
    const isTabletScreen = useAppSelector(state => (state as CustomAppState).xCommon.isTabletScreen);
    const registerMenu = useAppSelector(state => (state as CustomAppState).xHeader.registerMenu);

    const fullOverlay = registerMenu?.some(i => i?.includes("_sidepopup")) ?? false;
    const overlayRef = useRef<HTMLDivElement | null>(null);

    return (
        <Transition
            nodeRef={overlayRef}
            mountOnEnter
            unmountOnExit
            in={registerMenu && registerMenu.length > 0}
            timeout={{ enter: 75, exit: 250 }}
        >
            {state => (
                <StyledOverlayComponent
                    ref={overlayRef}
                    className={registerMenu && isTabletScreen ? "header-overlay" : ""}
                    transitionState={state}
                    fullOverlay={fullOverlay}
                />
            )}
        </Transition>
    );
};

export default StyledOverlay;
